import { useState, useEffect } from 'react';

import { apiService } from '../services/api';
import { getStaticData } from '../hooks/useStaticData';

import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
// import ContactModal from './ContactModal'; // TODO: re-enable if contact form returns

const ProgramsSection = () => {
  // Use static data as initial state to avoid flash of default content
  const [programs, setPrograms] = useState(() => getStaticData('programs') || []);
  const [businessInfo, setBusinessInfo] = useState(() => getStaticData('businessInfo'));
  const [loading, setLoading] = useState(() => {
    // Only show loading if we don't have static data
    const staticPrograms = getStaticData('programs');
    return !staticPrograms || staticPrograms.length === 0;
  });

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const programsData = await apiService.getPrograms();
        if (programsData) setPrograms(programsData);
      } catch (error) {
        console.error('Failed to load programs:', error);
      } finally {
        setLoading(false);
      }
    };
    const fetchBusinessInfo = async () => {
      const info = await apiService.getBusinessInfo();
      if (info) setBusinessInfo(info);
    };
    fetchPrograms();
    fetchBusinessInfo();
  }, []);

  // Programs with their own page link there; the rest call Hanshi Jim's direct line.
  const handleLearnMore = (program) => {
    window.location.href = program.link_url || 'tel:+13524280086';
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-64 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-300 rounded w-96 mx-auto mb-16"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-gray-300 h-96 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="programs" className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-gray-900 mb-3 md:mb-4">
            Choose Your Path
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            From young warriors to seasoned practitioners, we have the perfect program 
            to help you achieve your martial arts goals.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto">
          {programs.map((program) => (
            <Card key={program.id} className="group hover:shadow-2xl transition-all duration-300 border-0 shadow-lg overflow-hidden flex flex-col">
              <div className="relative">
                <img
                  src={program.image_url}
                  alt={program.title}
                  className="w-full h-48 md:h-56 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-3 right-3 md:top-4 md:right-4">
                  <Badge className="bg-red-600 text-white text-xs md:text-sm px-2 py-1 md:px-3 md:py-1">
                    {program.age_range}
                  </Badge>
                </div>
              </div>

              <CardHeader className="pb-3 md:pb-4">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 group-hover:text-red-600 transition-colors">
                  {program.title}
                </h3>
              </CardHeader>

              <CardContent className="flex-1 flex flex-col">
                <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-4 md:mb-6">
                  {program.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-4 md:mb-6">
                  {program.features.map((feature, index) => (
                    <Badge key={index} variant="outline" className="text-xs md:text-sm px-2 py-1">
                      {feature}
                    </Badge>
                  ))}
                </div>

                <div className="mt-auto pt-4">
                  <Button
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold text-sm md:text-base py-5 md:py-6"
                    onClick={() => handleLearnMore(program)}
                  >
                    Learn More
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Explore individual arts — SEO landing pages */}
        <div className="mt-10 md:mt-14 max-w-4xl mx-auto text-center">
          <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3">
            Looking for a specific art?
          </h3>
          <p className="text-sm md:text-base text-gray-600 mb-5 px-4">
            Owaza's curriculum blends multiple traditional martial arts. Read more about each:
          </p>
          <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm md:text-base">
            <li>
              <a href="/programs/kids-jujitsu" className="text-red-600 hover:text-red-700 hover:underline font-medium">
                Kids Jujitsu
              </a>
            </li>
            <li>
              <a href="/programs/aikido" className="text-red-600 hover:text-red-700 hover:underline font-medium">
                Aikido
              </a>
            </li>
            <li>
              <a href="/programs/karate-for-kids" className="text-red-600 hover:text-red-700 hover:underline font-medium">
                Karate for Kids
              </a>
            </li>
            <li>
              <a href="/programs/judo" className="text-red-600 hover:text-red-700 hover:underline font-medium">
                Judo
              </a>
            </li>
            <li>
              <a href="/programs/tai-chi" className="text-red-600 hover:text-red-700 hover:underline font-medium">
                Tai Chi
              </a>
            </li>
          </ul>
        </div>

        {/* Policy Notice */}
        <div className="mt-12 md:mt-16 text-center">
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 md:p-6 max-w-4xl mx-auto rounded-r-lg">
            <p className="text-sm md:text-base text-blue-800 font-medium">
              <strong>Important:</strong> Appointment required • No spectators •
              Adult memberships require a completed background check with consent.
            </p>
          </div>
        </div>
      </div>

      {/* ContactModal removed — all CTAs now call Jim Rivera's direct line tel:+13524280086 */}
      {/* TODO: re-enable <ContactModal> here if contact form returns */}
    </section>
  );
};

export default ProgramsSection;